/**
 * Create User Pass Hook — envelope-returning (internalapi-envelope).
 *
 * `mutationFn` returns the `ApiResult` UNCHANGED: a business failure
 * (declined card, `promotion_rejected`, …) is normal data on the success
 * path, not an exception — `apiClient` never throws, so TanStack's error
 * path is reserved for genuine programmer errors. Query invalidation is
 * gated on `result.success` (admin `QUERY_PATTERNS.md` §4, the repo's
 * mutation spec). Call sites branch via the split callbacks below, so
 * success bodies keep receiving the bare payload.
 *
 * @module subscription/hooks
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { passService } from '../api/passService';
import type { ApiErrorResult, ApiResult } from '~/lib/apiClient/types';
import type { CreateUserPassRequest, CreateUserPassResponse } from '../types';

export interface UseCreateUserPassOptions {
  /** Called when the API answered success — receives the bare payload. */
  onSuccess?: (data: CreateUserPassResponse, variables: CreateUserPassRequest) => void;

  /**
   * Called when the API answered `success: false` — the full failure envelope
   * (`message`, `error.code`, `error.details`, `httpStatus`). This is the
   * expected-failure path; read `getPromotionRejection(result)` here for the
   * promo auto-drop.
   */
  onFailure?: (result: ApiErrorResult, variables: CreateUserPassRequest) => void;

  /** Genuine exceptions only (programmer error) — apiClient never throws. */
  onError?: (error: Error, variables: CreateUserPassRequest) => void;
}

export interface UseCreateUserPassReturn {
  /** Mutation function — resolves the ENVELOPE for every API outcome. */
  createUserPass: (request: CreateUserPassRequest) => Promise<ApiResult<CreateUserPassResponse>>;

  /** Whether the mutation is currently in progress */
  isCreating: boolean;

  /** Reset the mutation state */
  reset: () => void;
}

/**
 * Create a user pass (purchase).
 *
 * Invalidates the club's subscription queries on a SUCCESSFUL create, scoped
 * off `variables.clubId`.
 */
export function useCreateUserPass(
  options: UseCreateUserPassOptions
): UseCreateUserPassReturn {
  const { onSuccess, onFailure, onError } = options;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (request: CreateUserPassRequest) => passService.createUserPass(request),

    onSuccess: (result, variables) => {
      if (!result.success) {
        onFailure?.(result, variables);
        return;
      }

      const requestClubId = variables.clubId;

      // Invalidate active subscription query
      queryClient.invalidateQueries({
        queryKey: queryKeys.subscriptions.active(requestClubId, variables.userId),
      });

      // Invalidate available subscriptions (club-level)
      queryClient.invalidateQueries({
        queryKey: queryKeys.subscriptions.all(requestClubId),
      });

      onSuccess?.(result.payload, variables);
    },

    onError: (error, variables) => {
      onError?.(error as Error, variables);
    },
  });

  return {
    createUserPass: mutation.mutateAsync,
    isCreating: mutation.isPending,
    reset: mutation.reset,
  };
}
